import { AccountRunStatus } from '@hit-game/shared/enums';
import type {
  BulkGroupAction,
  IAccountDataResponse,
  IAviatorProfile,
  IAviatorSessionMap,
  IGameRoundState,
  IGameRoundsView,
  IBulkGroupActionResult,
  IGroup,
} from '@hit-game/shared/types';
import {
  Activity,
  AlertTriangle,
  Dices,
  Gamepad2,
  LayoutGrid,
  ListChecks,
  Plane,
  Rows3,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PageHeader } from '../../components/account/PageHeader';
import { GroupConfigModal } from '../../components/group/GroupConfigModal';
import { AccountCard, type AccountCardActions } from '../../components/lobby/AccountCard';
import { AviatorConfigModal } from '../../components/lobby/AviatorConfigModal';
import { balancesFromAviatorSessions, mergeLiveBalances } from '../../components/lobby/liveBalance';
import { AccountInfoModal } from '../../components/lobby/AccountInfoModal';
import { BetLogModal } from '../../components/lobby/BetLogModal';
import { BulkActionBar } from '../../components/lobby/BulkActionBar';
import { BulkActionSheet, type SkippedGroup } from '../../components/lobby/BulkActionSheet';
import { BulkResultModal } from '../../components/lobby/BulkResultModal';
import { DepositModal } from '../../components/lobby/DepositModal';
import { GroupCard } from '../../components/lobby/GroupCard';
import { GroupSummaryCard } from '../../components/lobby/GroupSummaryCard';
import { GroupSessionModal } from '../../components/lobby/GroupSessionModal';
import { GameRoundCards, LobbySkeleton } from '../../components/lobby/GameRoundCards';
import { TransactionModal } from '../../components/lobby/TransactionModal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import { useGroupSelection } from '../../hooks/useGroupSelection';
import { fetchAllPages } from '../../lib/http';
import { groupEligibility, indexAccountsById } from '../../lib/group-eligibility';
import {
  acceptRoundsPacket,
  isPacketStaleOnArrival,
  type RoundsWatermark,
} from '../../lib/rounds-packet';
import { accountService } from '../../services/account.service';
import { aviatorService } from '../../services/aviator.service';
import { jackpotService } from '../../services/jackpot.service';
import { groupService } from '../../services/group.service';
import { useJackpotEvents } from '../../hooks/useJackpotEvents';
import { useSyncEvents } from '../../hooks/useSyncEvents';

/** Cảnh báo sau thao tác (vd proxy chết) — dài hơn toast lỗi thường để kịp đọc. */
const WARNING_TOAST_MS = 9_000;

type LobbyTab = 'table' | 'aviator';

/**
 * Hai tab = hai lối chơi, KHÔNG phải hai bộ lọc của cùng một danh sách.
 *
 * `table` (TX/XĐ) làm việc trên NHÓM; `aviator` (Máy bay) làm việc trên TỪNG tài
 * khoản lẻ — xem `docs/plans/solo-jackpot-hunt.md`.
 */
const LOBBY_TABS = [
  { key: 'table', label: 'Tài Xỉu / Xóc Đĩa', Icon: Dices },
  { key: 'aviator', label: 'Máy bay', Icon: Plane },
] as const satisfies readonly { key: LobbyTab; label: string; Icon: typeof Dices }[];

/**
 * Nhịp tự tải lại. Khớp với thời lượng thanh tiến trình ở đầu trang — hai con số
 * lệch nhau thì thanh chạy xong mà dữ liệu chưa đổi (hoặc ngược lại).
 */
const REFRESH_MS = 30_000;

/**
 * Nhịp hỏi trạng thái phiên — nhanh hơn hẳn nhịp tải lại cả sảnh.
 *
 * Hai nhịp khác nhau vì hai thứ khác nhau: tải lại sảnh là mấy chục bản ghi từ
 * Mongo, còn hỏi phiên chỉ đọc RAM của server và không chạm nhà cái. Một ván
 * Tài Xỉu chưa tới một phút, nên hỏi mỗi 30 giây là bảng phiên trễ nửa ván.
 */
const SESSION_POLL_MS = 5_000;

/**
 * Nhịp hỏi ván 4 game — chỉ còn là LƯỚI AN TOÀN, không phải nguồn chính.
 *
 * Nguồn chính giờ là SSE: observer đẩy gói ván ngay mỗi lần nó ghi Redis, nên
 * mọi mốc đáng kể (mở ván, cất cánh, nổ) về tới thẻ trong vài chục ms. Vòng hỏi
 * này chỉ để bù lúc SSE rớt hoặc bị proxy gom lô.
 *
 * Nên giãn từ 1.5s lên 5s: hỏi dày nữa là mấy chục request mỗi phút cho một dữ
 * liệu đã được đẩy tới nơi. Rẻ thật (một `GET` một khoá Redis) nhưng không miễn phí.
 */
const ROUNDS_POLL_MS = 5_000;

/**
 * Hai dải nhóm ngoài sảnh — chỉ khác tiêu đề và màu, thân hoàn toàn giống nhau.
 *
 * Trước đây là hai khối JSX chép đôi; sửa cách vẽ thẻ nhóm ở một khối là dải kia
 * lệch đi, mà hai dải nằm cạnh nhau trên cùng màn nên lệch thấy ngay.
 */
const GROUP_SECTIONS = [
  {
    key: 'running',
    title: 'NHÓM ĐANG HOẠT ĐỘNG',
    icon: Activity,
    titleClass: 'text-success',
    iconClass: '',
  },
  {
    key: 'idle',
    title: 'NHÓM CỦA TÔI',
    icon: Users,
    titleClass: 'text-ink',
    iconClass: 'text-accent',
  },
] as const;

export function LobbyPage() {
  const toast = useToast();
  const confirm = useConfirm();

  const [accounts, setAccounts] = useState<IAccountDataResponse[]>([]);
  const [groups, setGroups] = useState<IGroup[]>([]);
  const [loading, setLoading] = useState(true);
  /**
   * Nhịp tải gần nhất có hỏng không.
   *
   * `overview()` gọi với `_silent` nên interceptor KHÔNG bắn toast — cố ý, vì
   * poll 30 giây mà mạng chập thì toast dồn liên tục. Nhưng bản trước lại không
   * bù bằng gì cả: API chết là sảnh trắng trơn kèm dòng "Chưa có tài khoản",
   * đúng cái bẫy mà `lib/http.ts` viết doc để tránh. Trạng thái này thay chỗ đó
   * bằng một dải cảnh báo tại chỗ.
   */
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Đổi mỗi lần tải lại để thanh tiến trình chạy lại từ đầu. */

  const [betLogTarget, setBetLogTarget] = useState<IAccountDataResponse | null>(null);
  const [transactionTarget, setTransactionTarget] = useState<IAccountDataResponse | null>(null);
  const [depositTarget, setDepositTarget] = useState<IAccountDataResponse | null>(null);
  const [infoTarget, setInfoTarget] = useState<IAccountDataResponse | null>(null);
  /** Nhóm đang mở popup sửa cược — cùng popup với trang Nhóm. */
  const [configTarget, setConfigTarget] = useState<IGroup | null>(null);
  /** Nhóm đang mở bảng "Show Phiên". */
  const [sessionTarget, setSessionTarget] = useState<IGroup | null>(null);

  /**
   * Ván của MỌI nhóm đang chạy, theo `groupId`.
   *
   * Một vòng hỏi cho cả sảnh chứ không phải mỗi thẻ một vòng: nhóm nào cũng hỏi
   * cùng một endpoint, và hỏi lẻ là N request mỗi 5 giây cho cùng một câu trả lời.
   */
  const [gameRounds, setGameRounds] = useState<IGameRoundState[]>([]);
  /** Mốc (giờ trình duyệt) lúc nhận gói ván MỚI — xem `GameRoundCards.receivedAt`. */
  const [roundsAt, setRoundsAt] = useState(() => Date.now());
  /**
   * Gói ĐANG GIỮ đã cũ sẵn từ lúc tới nơi chưa — xem `isPacketStaleOnArrival`.
   *
   * Mở màn `true`: chưa nhận gói nào thì không có gì để tin. Chưa có gói cũng
   * đồng nghĩa `gameRounds` rỗng nên dải thẻ đang ẩn, nhưng để `false` ở đây là
   * đặt sẵn một mặc định "cứ tin đi" cho mọi đường thêm vào sau này.
   */
  const [roundsStaleOnArrival, setRoundsStaleOnArrival] = useState(true);
  /** Mốc xếp thứ tự của gói ĐANG GIỮ (`rev` + luồng), xem `applyRoundsPacket`. */
  const roundsWatermark = useRef<RoundsWatermark | null>(null);

  /**
   * Tải lại sảnh. TỰ NUỐT lỗi thành `loadError` thay vì ném ra ngoài.
   *
   * Mọi nơi gọi hàm này đều gọi kiểu "bắn rồi quên" (`void reload()` trong hẹn
   * giờ, trong callback của thẻ nhóm). Ném ra từ đó là một unhandled rejection
   * mỗi 30 giây khi API chết — không ai bắt, không ai thấy.
   */
  const reload = useCallback(async () => {
    try {
      const [accountList, groupList] = await Promise.all([
        accountService.overview(),
        // MỌI nhóm, không phải một trang — sảnh vẽ hết nhóm cùng lúc, thiếu nhóm
        // nào là tài khoản của nó rơi nhầm sang mục "chưa phân nhóm".
        fetchAllPages((params) => groupService.list(params)),
      ]);
      setAccounts(accountList);
      setGroups(groupList);
      setLoadError(null);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Không tải được dữ liệu sảnh');
    }
  }, []);

  // `reload` không phụ thuộc state nào (`useCallback` deps rỗng) nên định danh
  // của nó cố định — hẹn giờ dựng đúng MỘT lần và chạy đủ nhịp. Bản trước giữ nó
  // trong một `ref` được gán NGAY TRONG THÂN RENDER, thứ React cấm vì render phải
  // thuần; cái ref đó cũng thừa vì hàm vốn đã ổn định.
  useEffect(() => {
    void reload().finally(() => {
      setLoading(false);
    });

    const timer = setInterval(() => {
      void reload();
    }, REFRESH_MS);

    return () => {
      clearInterval(timer);
    };
  }, [reload]);

  // Đồng bộ worker xong → server đẩy SSE về đây → tải lại sảnh NGAY, không đợi
  // hết nhịp 30s. Vòng poll ở trên vẫn giữ làm lưới an toàn khi SSE rớt/bị proxy
  // gom lô. `reload` ổn định (deps rỗng) nên connection không dựng lại mỗi render.
  useSyncEvents(() => {
    void reload();
  });

  /**
   * Cửa VÀO DUY NHẤT của gói ván — vòng hỏi lẫn SSE đều phải đi qua đây.
   *
   * Hai đường đó KHÔNG có thứ tự với nhau: SSE đẩy gói P2 lúc t=0, một request
   * hỏi đã bay đi từ trước hạ cánh lúc t=50ms mang theo gói P1 CŨ HƠN. Bản trước
   * để mỗi đường tự xử: đường hỏi ghi đè state bằng P1, và vì nó chỉ so `!==`
   * (khác nhau, không theo thứ tự thời gian) nên còn coi P1 là gói mới mà dời mốc
   * nhận về hiện tại. Hệ số Máy bay CŨ được đóng dấu "vừa nhận" → chốt
   * `MAX_ODD_AGE_MS` trong `GameRoundCards` không bao giờ chạm → thẻ hiện số cũ
   * như thể đang đúng. Với nhịp hỏi 5s và SSE ~2 frame/giây lúc đang bay, cửa sổ
   * va chạm đó lặp lại mỗi chu kỳ hỏi.
   *
   * Nên: chỉ nhận gói MỚI HƠN cái đang giữ (xem {@link acceptRoundsPacket}), và khi
   * nhận thì cập nhật `games` + ref + mốc CÙNG NHAU. Nửa vời — ghi `games` trước,
   * độc lập với quyết định — chính là chỗ dữ liệu cũ lọt qua.
   *
   * Mốc so lấy từ chính quyết định chứ không lấy từ gói: gói `disconnected` do bên
   * ĐỌC dựng (khoá Redis vắng) không có chỗ nào trên trục thứ tự, nên nó được nhận
   * để xoá thẻ nhưng KHÔNG được thay mốc — gán mốc `null` theo nó là ngay sau đó
   * một vòng hỏi cũ đang bay hạ cánh, không còn gì để so, và dựng lại đúng mấy thẻ
   * vừa xoá.
   *
   * So sánh PHẢI làm ở đây, không nhét vào updater của `setRoundsAt`: React chạy
   * updater ở render phase, tức SAU khi dòng gán ref đã thực thi — lúc đó
   * `roundsWatermark.current` luôn bằng mốc của gói vừa xử lý, updater luôn trả
   * `prev`, và mốc đứng chết ở thời điểm mount. Quá vài giây sau đó mọi gói đều bị
   * coi là quá hạn nên hệ số Máy bay KHÔNG bao giờ hiện.
   *
   * `watermarkAtRequest` chỉ đường HỎI truyền vào (xem vòng hỏi bên dưới): nó là
   * mốc so chụp lúc request bay đi, dùng để biết có gói nào mới hơn đã tới bằng
   * đường kia trong lúc chờ hay không.
   *
   * Gói cũ sẵn từ lúc tới nơi thì VẪN NHẬN, chỉ hạ cờ tin cậy. Bỏ hẳn gói mới là
   * cách xử lý sai ở đây: máy có đồng hồ lệch vài phút sẽ thấy MỌI gói đều "cũ",
   * và xử lý kiểu bỏ thì sảnh của máy đó trắng trơn vĩnh viễn, trong khi cái sai
   * thật chỉ nằm ở một ô hệ số.
   */
  const applyRoundsPacket = useCallback(
    (packet: IGameRoundsView, watermarkAtRequest?: RoundsWatermark | null) => {
      const { accept, watermark } = acceptRoundsPacket(
        packet,
        roundsWatermark.current,
        watermarkAtRequest,
      );
      if (!accept) return;
      const receivedAt = Date.now();
      roundsWatermark.current = watermark;
      setGameRounds(packet.games);
      setRoundsAt(receivedAt);
      setRoundsStaleOnArrival(isPacketStaleOnArrival(packet.updatedAt, receivedAt));
    },
    [],
  );

  /**
   * Ván hiện tại của 4 game — dựng từ TÀI KHOẢN BÁO HŨ, không phải từ nhóm.
   *
   * Nên nó đúng kể cả khi người dùng chưa bật nhóm hay tài khoản nào. Server trả
   * MỐC đóng cược nên nhịp hỏi vài giây là đủ; đồng hồ do thẻ tự đếm tại chỗ.
   */
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      // Chụp mốc so TRƯỚC khi gửi, không đọc lại lúc hạ cánh: gói `disconnected`
      // rỗng (server dựng khi khoá Redis vắng) không xếp được thứ tự nên nó được
      // nhận vô điều kiện — và trong lúc response này bay, một frame SSE MỚI HƠN
      // có thể đã cập nhật thẻ. Không có ảnh chụp lúc khởi hành thì gói cũ đó xoá
      // mất thẻ mới mà mốc so vẫn nguyên, nên vòng hỏi kế tiếp trả về cùng `rev`
      // lại bị từ chối — thẻ không dựng lại được tới lượt ghi sau của observer.
      const watermarkAtRequest = roundsWatermark.current;
      try {
        const view = await jackpotService.rounds();
        if (!alive) return;
        applyRoundsPacket(view, watermarkAtRequest);
      } catch {
        // `_silent` — không bắn toast cho một vòng hỏi lặp.
      }
    };

    void poll();
    const timer = setInterval(() => void poll(), ROUNDS_POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [applyRoundsPacket]);

  /**
   * Ván vừa đổi → observer đẩy SSE về đây, cập nhật NGAY thay vì đợi hết nhịp hỏi.
   *
   * Thứ này quan trọng nhất ở hai mốc: ván Máy bay NỔ (thẻ phải bỏ hệ số đang bay
   * ngay, không treo con số cuối suốt 5 giây tới nhịp hỏi sau) và observer MẤT
   * KẾT NỐI (server ghi đè gói `disconnected` + đẩy frame này, nếu không thì thẻ
   * đếm ngược về 0 rồi treo "ĐANG QUAY" với ván đã xong). Server gọi ghi với
   * `force` ở mọi mốc chuyển pha nên frame về đúng lúc.
   *
   * Đi CHUNG channel với hũ (`event: jackpot`) — frame `state` và `win` cũng về
   * đúng đường này, nên phải lọc `type` tường minh. Dùng {@link useJackpotEvents}
   * thay vì tự parse: hợp đồng kiểu của channel nằm ở đúng MỘT chỗ, và nó đã bọc
   * sẵn try/catch cho frame hỏng định dạng.
   */
  useJackpotEvents((event) => {
    if (event.type !== 'rounds') return;
    // Đi chung {@link applyRoundsPacket} với vòng hỏi, không tự cập nhật lấy: hai
    // đường mà hai luật thì đường này ghi ref xong đường kia vẫn nhận gói cũ hơn
    // của nó là gói mới — đúng cái race mà helper sinh ra để chặn.
    applyRoundsPacket(event);
  });

  // ── Máy bay (Aviator) ────────────────────────────────────────────────
  const [aviatorSessions, setAviatorSessions] = useState<IAviatorSessionMap>({});
  const [aviatorProfiles, setAviatorProfiles] = useState<Record<string, IAviatorProfile>>({});
  const [aviatorTarget, setAviatorTarget] = useState<IAccountDataResponse | null>(null);
  const [aviatorSaving, setAviatorSaving] = useState(false);

  /**
   * Nhịp hỏi RIÊNG, không ghép vào vòng poll nhóm.
   *
   * Cửa sổ cược Aviator chỉ dài ~5 giây (so với 20–46 giây của TX/XĐ), nên thanh
   * phiên phải được làm mới dày hơn thì con số trên thẻ mới bám kịp ván.
   */
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { sessions: next } = await aviatorService.sessions();
        if (alive) {
          setAviatorSessions(next);
          // Thẻ đang chạy bám số dư socket theo từng ván, khỏi đợi nhịp tải lại 30 giây.
          setAccounts((prev) => mergeLiveBalances(prev, balancesFromAviatorSessions(next)));
        }
      } catch {
        // `_silent` — không bắn toast cho một vòng hỏi lặp.
      }
    };

    void poll();
    const timer = setInterval(() => void poll(), SESSION_POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  /**
   * Số dư SỐNG của nhóm đang chạy (Tài Xỉu / Xóc Đĩa / MD5).
   *
   * Không chơi thì thôi — sảnh vẫn tải lại 30 giây như cũ. Có tài khoản đang chạy thì
   * hỏi dày (cùng nhịp phiên Máy bay) và ghép số socket vào thẻ, để số dư trên thẻ khớp
   * tin Telegram thay vì chậm vài ván. Bật/tắt theo `hasRunningAccount` nên nhóm dừng là
   * ngừng hỏi, không tốn request nền.
   */
  const hasRunningAccount = useMemo(
    () => accounts.some((item) => item.status === AccountRunStatus.RUNNING),
    [accounts],
  );
  useEffect(() => {
    if (!hasRunningAccount) return;

    let alive = true;
    const poll = async () => {
      try {
        const { balances } = await groupService.liveBalances();
        if (alive) setAccounts((prev) => mergeLiveBalances(prev, balances));
      } catch {
        // `_silent` — vòng hỏi lặp, hỏng một nhịp thì nhịp sau hỏi lại.
      }
    };

    void poll();
    const timer = setInterval(() => void poll(), SESSION_POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [hasRunningAccount]);

  /** Cấu hình đọc theo DANH SÁCH tài khoản hiện có — một request, không phải N. */
  const loadAviatorProfiles = useCallback(async (ids: string[]) => {
    if (ids.length === 0) {
      setAviatorProfiles({});
      return;
    }
    try {
      const { profiles } = await aviatorService.profiles(ids);
      setAviatorProfiles(Object.fromEntries(profiles.map((item) => [item.accountId, item])));
    } catch {
      // `_silent` — thiếu cấu hình chỉ làm nút Start tắt, không đáng một toast.
    }
  }, []);

  /** Thay MỘT thẻ tại chỗ — khỏi tải lại cả danh sách sau mỗi thao tác lẻ. */
  const replaceAccount = (updated: IAccountDataResponse) => {
    setAccounts((prev) =>
      prev.map((item) => (String(item._id) === String(updated._id) ? updated : item)),
    );
  };

  /**
   * Thao tác lẻ trên một thẻ: gọi API rồi thay ĐÚNG thẻ đó tại chỗ.
   *
   * Nuốt lỗi là cố ý — interceptor axios đã bắn toast, và thẻ giữ nguyên dữ liệu
   * cũ thay vì biến mất. Ba nút đăng nhập / đăng xuất / làm mới số dư chỉ khác
   * nhau ở lời gọi API nên dùng chung một khối.
   */
  const applyToCard = async (call: () => Promise<IAccountDataResponse>) => {
    try {
      const { warning, ...account } = await call();
      replaceAccount(account);
      // Thao tác xong nhưng có chuyện cần biết (vd đăng xuất qua proxy chết) — hiện lâu để kịp đọc.
      if (warning) toast.error(`${account.username}: ${warning}`, WARNING_TOAST_MS);
    } catch {
      // Toast lỗi do interceptor bắn.
    }
  };

  /**
   * Start/Stop Máy bay. Hỏi lại phiên NGAY sau khi gọi thay vì đợi hết nhịp poll:
   * nút Start/Stop bật tắt theo `aviatorSessions`, nên chờ tới 3 giây mới đổi thì
   * người dùng bấm Start xong thấy nút vẫn như cũ và bấm lần nữa.
   */
  const refreshAviatorSessions = async () => {
    try {
      const { sessions: next } = await aviatorService.sessions();
      setAviatorSessions(next);
      setAccounts((prev) => mergeLiveBalances(prev, balancesFromAviatorSessions(next)));
    } catch {
      // `_silent`.
    }
  };

  const aviatorControls = {
    onStart: async (id: string) => {
      try {
        await aviatorService.start(id);
        toast.success('Đã bật Máy bay');
      } catch {
        // Toast lỗi do interceptor bắn.
      }
      await refreshAviatorSessions();
      await reload();
    },
    onStop: async (id: string) => {
      try {
        const outcome = await aviatorService.stop(id);
        if (outcome.deferred) {
          toast.info(
            `Sẽ dừng sau khi ván${outcome.sessionId ? ` #${String(outcome.sessionId)}` : ''} kết thúc — không đặt ván mới`,
          );
        } else {
          toast.success('Đã dừng Máy bay');
        }
      } catch {
        // Toast lỗi do interceptor bắn.
      }
      await refreshAviatorSessions();
      await reload();
    },
    onConfig: setAviatorTarget,
  };

  const saveAviatorProfile = async (input: {
    amount: number;
    odd: number;
    maxRounds: number;
    maxTotalStake: number;
  }) => {
    if (!aviatorTarget) return;
    setAviatorSaving(true);
    try {
      const { profile } = await aviatorService.save(String(aviatorTarget._id), input);
      setAviatorProfiles((prev) => ({ ...prev, [profile.accountId]: profile }));
      toast.success('Đã lưu cấu hình Máy bay');
      setAviatorTarget(null);
    } catch {
      // Toast lỗi do interceptor bắn — giữ hộp thoại mở để sửa lại.
    } finally {
      setAviatorSaving(false);
    }
  };

  const deleteAccount = async (account: IAccountDataResponse) => {
    const accepted = await confirm({
      title: 'Xoá tài khoản',
      message: `Xoá "${account.username}"? Lịch sử cược và giao dịch của tài khoản này cũng bị xoá theo, proxy được trả về kho.`,
      confirmText: 'Xoá',
      type: 'danger',
    });
    if (!accepted) return;

    try {
      await accountService.remove(String(account._id));
      await reload();
    } catch {
      toast.error('Không xoá được tài khoản');
    }
  };

  const actions: AccountCardActions = {
    onLogin: (id) => applyToCard(() => accountService.login(id)),
    onLogout: (id) => applyToCard(() => accountService.logout(id)),
    onRefreshBalance: (id) => applyToCard(() => accountService.refreshBalance(id)),
    onShowBetLogs: setBetLogTarget,
    onShowTransactions: setTransactionTarget,
    onShowDeposit: setDepositTarget,
    onShowInfo: setInfoTarget,
    onDelete: (account) => {
      void deleteAccount(account);
    },
  };

  // ── Chia nhóm ────────────────────────────────────────────────────────
  const { runningGroups, idleGroups, ungrouped } = useMemo(() => {
    const runningIds = new Set(
      accounts
        .filter((item) => item.status === AccountRunStatus.RUNNING)
        .map((item) => String(item._id)),
    );
    const groupedIds = new Set(
      groups.flatMap((group) => group.members.map((member) => String(member.accountId))),
    );

    return {
      runningGroups: groups.filter((group) =>
        group.members.some((member) => runningIds.has(String(member.accountId))),
      ),
      idleGroups: groups.filter(
        (group) => !group.members.some((member) => runningIds.has(String(member.accountId))),
      ),
      ungrouped: accounts.filter((item) => !groupedIds.has(String(item._id))),
    };
  }, [accounts, groups]);

  // Chỉ hỏi cấu hình của tài khoản CHƯA vào nhóm — đó là tập duy nhất chơi Máy
  // bay được, và danh sách tài khoản thì tải lại mỗi 30 giây.
  const ungroupedIds = useMemo(
    () => ungrouped.map((item) => String(item._id)).join(','),
    [ungrouped],
  );
  useEffect(() => {
    void loadAviatorProfiles(ungroupedIds ? ungroupedIds.split(',') : []);
  }, [ungroupedIds, loadAviatorProfiles]);

  // ── Thao tác nhiều nhóm (bulk start/stop) ────────────────────────────
  const selection = useGroupSelection();
  const accountsById = useMemo(() => indexAccountsById(accounts), [accounts]);

  /**
   * Tab game. Tách vì hai lối chơi KHÔNG cùng đơn vị vận hành:
   * TX/XĐ chạy theo NHÓM 4 tài khoản đối ứng hai phe, còn Máy bay (Aviator) chơi
   * LẺ từng tài khoản. Trộn chung một danh sách thì thẻ nhóm và thẻ tài khoản nằm
   * cạnh nhau như hai thứ cùng loại, trong khi Start ở hai bên nghĩa khác hẳn nhau.
   */
  const [tab, setTab] = useState<LobbyTab>(() => {
    try {
      return localStorage.getItem('lobby-tab') === 'aviator' ? 'aviator' : 'table';
    } catch {
      return 'table';
    }
  });
  const setTabMode = useCallback(
    (next: LobbyTab) => {
      setTab(next);
      // Rời tab nhóm thì bỏ luôn chế độ chọn: thanh thao tác hàng loạt bị ẩn theo
      // tab, nên giữ lại là để một lựa chọn đang sống mà không có gì trên màn hình
      // nói ra — quay lại tab cũ sẽ thấy nhóm "tự nhiên" đang được chọn.
      if (next !== 'table') selection.clear();
      try {
        localStorage.setItem('lobby-tab', next);
      } catch {
        // localStorage có thể bị chặn (ẩn danh) — không nhớ được thì thôi.
      }
    },
    [selection],
  );

  /** Chi tiết (thẻ đầy đủ) ⇄ Tóm tắt (thẻ gọn để chọn nhiều nhóm). Nhớ qua reload. */
  const [view, setView] = useState<'detail' | 'summary'>(() => {
    try {
      return localStorage.getItem('lobby-view') === 'summary' ? 'summary' : 'detail';
    } catch {
      return 'detail';
    }
  });
  const setViewMode = useCallback((next: 'detail' | 'summary') => {
    setView(next);
    try {
      localStorage.setItem('lobby-view', next);
    } catch {
      // localStorage có thể bị chặn (ẩn danh) — không nhớ được thì thôi, không chặn UI.
    }
  }, []);

  /** Chọn được ở CẢ hai dải — nên gộp lại một danh sách. */
  const allGroups = useMemo(() => [...runningGroups, ...idleGroups], [runningGroups, idleGroups]);
  const selectedGroups = useMemo(
    () => allGroups.filter((group) => selection.selected.has(String(group._id))),
    [allGroups, selection.selected],
  );

  /** Số nhóm đủ điều kiện từng hành động — hiện trên badge của thanh bulk. */
  const eligibleCounts = useMemo(() => {
    let start = 0;
    let stop = 0;
    for (const group of selectedGroups) {
      const eligibility = groupEligibility(group, accountsById);
      if (eligibility.start.ok) start += 1;
      if (eligibility.stop.ok) stop += 1;
    }
    return { start, stop };
  }, [selectedGroups, accountsById]);

  /** Nhóm đang chờ xác nhận trong bảng bulk. */
  const [pending, setPending] = useState<{
    action: BulkGroupAction;
    run: IGroup[];
    skip: SkippedGroup[];
  } | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResults, setBulkResults] = useState<{
    action: BulkGroupAction;
    results: IBulkGroupActionResult[];
  } | null>(null);

  /** Bấm một hành động ở thanh bulk → chia đủ/không đủ điều kiện rồi mở bảng. */
  const openBulk = (action: BulkGroupAction) => {
    const run: IGroup[] = [];
    const skip: SkippedGroup[] = [];
    for (const group of selectedGroups) {
      const verdict = groupEligibility(group, accountsById)[action];
      if (verdict.ok) run.push(group);
      else skip.push({ group, reason: verdict.reason });
    }
    setPending({ action, run, skip });
  };

  const confirmBulk = async () => {
    if (!pending) return;
    setBulkBusy(true);
    try {
      const { results } = await groupService.bulkAction(
        pending.action,
        pending.run.map((group) => String(group._id)),
      );
      setBulkResults({ action: pending.action, results });
      setPending(null);
      selection.toggleSelecting(); // thoát chế độ chọn + xoá lựa chọn
      await reload();
    } catch {
      // Toast lỗi do interceptor bắn.
    } finally {
      setBulkBusy(false);
    }
  };

  const toggleSelectAll = () => {
    const allIds = allGroups.map((group) => String(group._id));
    selection.replace(selection.selected.size === allIds.length ? [] : allIds);
  };

  /**
   * Lưu cấu hình nhóm từ popup ngay tại sảnh.
   *
   * Sảnh không phân trang nhóm nên tải lại được cả màn — khỏi phải đá người dùng
   * sang trang Nhóm chỉ để sửa mệnh giá rồi lại đi ngược về.
   */
  const saveGroupConfig = async (group: IGroup, input: Partial<IGroup>): Promise<boolean> => {
    try {
      await groupService.update(String(group._id), input);
      toast.success('Đã lưu nhóm');
      await reload();
      return true;
    } catch {
      // Toast lỗi do interceptor bắn.
      return false;
    }
  };

  if (loading) return <LobbySkeleton />;

  return (
    <div className="text-ink space-y-6">
      <PageHeader icon={Gamepad2} title="Sảnh" subtitle="Quản lý và theo dõi hoạt động chơi game" />

      {loadError && (
        <div
          role="alert"
          className="border-danger-border bg-danger-bg text-danger flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            {loadError} — số liệu bên dưới là của lần tải gần nhất, thử lại sau{' '}
            {String(REFRESH_MS / 1000)} giây.
          </span>
        </div>
      )}

      <GameRoundCards
        games={gameRounds}
        receivedAt={roundsAt}
        staleOnArrival={roundsStaleOnArrival}
      />

      <div className="border-line flex gap-1 border-b" role="tablist">
        {LOBBY_TABS.map(({ key, label, Icon }) => {
          const active = tab === key;
          const count = key === 'table' ? allGroups.length : ungrouped.length;

          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => {
                setTabMode(key);
              }}
              // Viền dưới thay vì nền: thanh này nằm ngay trên các thẻ nhóm/tài
              // khoản vốn đã là khối có nền — thêm một khối nền nữa thì hai thứ
              // trông cùng cấp, mà tab là thứ CHỨA chúng.
              className={`-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
                active
                  ? 'border-accent text-accent'
                  : 'text-ink-dim hover:text-ink border-transparent'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                  active ? 'bg-accent-soft text-accent' : 'bg-panel text-ink-dim'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {tab === 'table' && allGroups.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* Chi tiết ⇄ Tóm tắt — chế độ xem, độc lập với việc chọn. */}
          <div className="border-line bg-panel inline-flex rounded-lg border p-0.5">
            {(
              [
                { key: 'detail', label: 'Chi tiết', Icon: Rows3 },
                { key: 'summary', label: 'Tóm tắt', Icon: LayoutGrid },
              ] as const
            ).map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setViewMode(key);
                }}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                  view === key ? 'bg-accent-soft text-accent' : 'text-ink-dim hover:text-ink'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            {selection.selecting && (
              <button
                type="button"
                onClick={toggleSelectAll}
                className="text-ink-dim hover:text-ink rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors"
              >
                {selection.selected.size === allGroups.length ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
              </button>
            )}
            <button
              type="button"
              onClick={selection.toggleSelecting}
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                selection.selecting
                  ? 'border-accent-border bg-accent-soft text-accent'
                  : 'border-line bg-panel text-ink-dim hover:text-ink'
              }`}
            >
              <ListChecks className="h-4 w-4" />
              {selection.selecting ? 'Xong' : 'Chọn nhóm'}
            </button>
          </div>
        </div>
      )}

      {tab === 'table' &&
        GROUP_SECTIONS.map(({ key, title, icon: Icon, titleClass, iconClass }) => {
          const sectionGroups = key === 'running' ? runningGroups : idleGroups;
          if (sectionGroups.length === 0) return null;

          return (
            <section key={key} className="space-y-4">
              <h4 className={`flex items-center gap-2 text-sm font-bold ${titleClass}`}>
                <Icon className={`h-4 w-4 ${iconClass}`} />
                {title}
              </h4>
              <div
                className={
                  view === 'summary'
                    ? 'grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3'
                    : 'grid grid-cols-1 gap-4 xl:grid-cols-2'
                }
              >
                {sectionGroups.map((group) =>
                  view === 'summary' ? (
                    <GroupSummaryCard
                      key={String(group._id)}
                      group={group}
                      accountsById={accountsById}
                      selectable={selection.selecting}
                      selected={selection.selected.has(String(group._id))}
                      onToggleSelect={selection.toggle}
                    />
                  ) : (
                    <GroupCard
                      key={String(group._id)}
                      group={group}
                      accounts={accounts}
                      onChanged={() => {
                        void reload();
                      }}
                      onEdit={setConfigTarget}
                      onShowSessions={setSessionTarget}
                      selectable={selection.selecting}
                      selected={selection.selected.has(String(group._id))}
                      onToggleSelect={selection.toggle}
                      {...actions}
                    />
                  ),
                )}
              </div>
            </section>
          );
        })}

      {/* Chưa có nhóm nào thì tab này KHÔNG còn gì để vẽ: hai dải nhóm đều rỗng
          nên `.map` trả null hết, và mục "tài khoản chưa phân nhóm" đã dời sang
          tab Máy bay. Không có khối này thì người dùng mới (chưa lập nhóm) mở
          sảnh ra thấy một trang trắng. */}
      {tab === 'table' && allGroups.length === 0 && (
        <div className="border-line flex flex-col items-center justify-center rounded-xl border-2 border-dashed py-10 text-center">
          <Dices className="text-ink-dim mb-2 h-8 w-8" />
          <span className="text-ink-muted text-sm font-medium">
            Chưa có nhóm nào. Tạo ở mục &quot;Nhóm&quot; — Tài Xỉu / Xóc Đĩa cần đúng 4 tài khoản
            chia hai phe.
          </span>
        </div>
      )}

      {/* Máy bay chơi LẺ từng tài khoản — nên tab này liệt kê đúng những tài khoản
          CHƯA vào nhóm. Tài khoản đã ở trong nhóm TX/XĐ không hiện ở đây: nhóm giữ
          ảnh chụp cấu hình và tự đặt lệnh mỗi ván, cho nó chơi thêm Máy bay là hai
          vòng đánh cùng tiêu một số dư mà không bên nào biết bên nào. */}
      {tab === 'aviator' && (
        <section className="space-y-4">
          <h4 className="text-ink flex items-center gap-2 text-sm font-bold">
            <Plane className="text-accent h-4 w-4" />
            TÀI KHOẢN CHƠI LẺ
          </h4>

          {ungrouped.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {ungrouped.map((account) => {
                const id = String(account._id);
                return (
                  <AccountCard
                    key={id}
                    account={account}
                    aviator={{
                      configured: aviatorProfiles[id] !== undefined,
                      // Đọc phiên Aviator RIÊNG, KHÔNG dùng `account.status`: cờ đó
                      // bật cho cả nhóm lẫn Aviator nên không phân biệt được.
                      running: aviatorSessions[id] !== undefined,
                      ...aviatorControls,
                    }}
                    {...actions}
                  />
                );
              })}
            </div>
          ) : (
            <div className="border-line flex flex-col items-center justify-center rounded-xl border-2 border-dashed py-10 text-center">
              <Plane className="text-ink-dim mb-2 h-8 w-8" />
              <span className="text-ink-muted text-sm font-medium">
                {accounts.length > 0
                  ? 'Mọi tài khoản đều đã vào nhóm — Máy bay chỉ chơi bằng tài khoản chưa phân nhóm.'
                  : 'Chưa có tài khoản. Thêm ở mục "Kho tài khoản".'}
              </span>
            </div>
          )}
        </section>
      )}

      <GroupSessionModal
        group={sessionTarget}
        onClose={() => {
          setSessionTarget(null);
        }}
      />

      <BetLogModal
        account={betLogTarget}
        onClose={() => {
          setBetLogTarget(null);
        }}
      />
      <TransactionModal
        account={transactionTarget}
        onClose={() => {
          setTransactionTarget(null);
        }}
      />
      <DepositModal
        account={depositTarget}
        onClose={() => {
          setDepositTarget(null);
        }}
      />
      <AccountInfoModal
        account={infoTarget}
        onClose={() => {
          setInfoTarget(null);
        }}
      />
      <AviatorConfigModal
        account={aviatorTarget}
        profile={aviatorTarget ? (aviatorProfiles[String(aviatorTarget._id)] ?? null) : null}
        busy={aviatorSaving}
        running={aviatorTarget ? aviatorSessions[String(aviatorTarget._id)] !== undefined : false}
        onClose={() => {
          setAviatorTarget(null);
        }}
        onSubmit={(input) => {
          void saveAviatorProfile(input);
        }}
      />
      <GroupConfigModal
        group={configTarget}
        accounts={accounts}
        onClose={() => {
          setConfigTarget(null);
        }}
        onSubmit={saveGroupConfig}
      />

      {/* Chỉ thuộc tab nhóm — Máy bay không có thao tác hàng loạt nào ở cấp nhóm. */}
      {tab === 'table' && (
        <BulkActionBar
          count={selection.selected.size}
          startCount={eligibleCounts.start}
          stopCount={eligibleCounts.stop}
          busy={bulkBusy}
          onAction={openBulk}
          onClear={selection.clear}
        />
      )}
      {pending && (
        <BulkActionSheet
          open
          action={pending.action}
          run={pending.run}
          skip={pending.skip}
          busy={bulkBusy}
          onConfirm={() => {
            void confirmBulk();
          }}
          onClose={() => {
            if (!bulkBusy) setPending(null);
          }}
        />
      )}
      <BulkResultModal
        data={bulkResults}
        onClose={() => {
          setBulkResults(null);
        }}
      />
    </div>
  );
}
